import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogActions, MatDialogClose, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatToolbarModule } from '@angular/material/toolbar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Pop } from 'src/app/classes/shared/pop';
import { Tools } from 'src/app/classes/shared/tools';
import { MetricModel } from 'src/app/interfaces/api/project';
import { User } from 'src/app/interfaces/api/user';
import { MetricService } from 'src/app/services/api/metrics/metric.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { DatePickerComponent } from 'ui-library';

@Component({
  selector: 'app-metric-add',
  templateUrl: './metric-add.component.html',
  styleUrls: ['./metric-add.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatToolbarModule,
    MatIconModule,
    MatDialogClose,
    MatDialogActions,
    MatTabsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
    MatCheckboxModule,
    MatProgressBarModule,
    MatDatepickerModule,
    TranslateModule,
    DatePickerComponent
  ]
})
export class MetricAddComponent implements OnDestroy {
  metricModels: MetricModel[] = [];
  inputs: any[] = [];
  payload: any[] = [];
  currentModel: MetricModel | undefined;
  currentModelIndex: any = 0;
  user: User;
  destroy$ = new Subject<void>();
  // srcResult: any;
  uploadInProgress = false;
  uploadError: string = '';
  uploadSuccess: string = '';
  uploadProgress: number = 0;
  uploadedFileName: string | null = null;
  startDateMetric: string = '';
  endDateMetric: string = '';
  isEdit: boolean = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private metricService: MetricService,
    private tools: Tools,
    private pop: Pop,
    private translate: TranslateService,
    private dialogRef: MatDialogRef<MetricAddComponent>,
    private auth: AuthService,
    private router: Router) {
    this.user = this.auth.currentUser();

    // this.projectsService.currentProject.pipe(takeUntil(this.destroy$)).subscribe(project => {
    //   if (this.project && this.project.id != project.id) {
    //     this.project = project;
    //     this.ngOnInit();
    //   } else {
    //     this.project = project;
    //   }
    // });

    this.metricModels = data.metricModels || [];

    if (this.metricModels.length && this.metricModels[0]) {
      this.currentModel = this.metricModels[0];
      this.isEdit = !!data.edit;
      const payload = this.isEdit ? data.edit.payload : data.add;

      if (data.add) {
        let startDate = moment(payload.day, 'YYYY-MM-DD');
        const startHour = moment(payload.startHour).format('HH:mm');
        const endHour = moment(payload.endHour).format('HH:mm');

        this.currentModel.payload?.forEach((p) => {
          if (p.label == "startDate") {
            p.value = startDate.format('YYYY-MM-DD') + 'T' + startHour + ':00';
          } else if (p.label == "endDate") {
            p.value = startDate.format('YYYY-MM-DD') + 'T' + endHour + ':00';
          }
        });
      }

      if (data.edit) {
        this.currentModel.payload?.forEach((p) => {
          const key: string = p.label || "";

          if (payload[key] !== undefined) {
            p.value = payload[key]?.toString();
          } else if (this.isEdit && data.edit[key] !== undefined) {
            p.value = data.edit[key]?.toString();
          }
        });
      }

      if (data.create) {
        this.metricModels.forEach((m) => {
          m.payload?.forEach((p) => {
            if (p.label == "startDate")
              p.value = data.startDate;
            if (p.label == "endDate")
              p.value = moment(data.startDate).add(1, 'hour').toISOString();
          });
        });
      }
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onModelChanged(event: any) {
    if (this.metricModels[event.value])
      this.currentModel = this.metricModels[event.value];
  }

  onDateChange(dateEvent: any, config: any): void {
    config.value = dateEvent.startDate || dateEvent;
  }

  saveMetric() {
    let payload: any = {};
    this.currentModel?.payload?.forEach((p) => {
      if (!p.label) return;
      if (p.type == 'date') {
        payload[p.label] = moment(p.value).toISOString();
      } else {
        payload[p.label] = p.value;
      }
    });

    payload['customMetric'] = true;
    payload['customId'] = this.currentModel?._id;

    let metric: any = {
      name: this.currentModel?.name,
      user: this.user.id,
      userEmail: this.user.email,
      createdDate: payload['startDate'],
      payload
    };

    if (!this.isEdit) {
      this.metricService.addMetric(metric).pipe(takeUntil(this.destroy$)).subscribe((res) => {
        this.pop.toast(this.translate.instant('events.metric_saved'), 'success', 4000, 'add');
        this.dialogRef.close(metric);
      },
        (error) => {
          this.pop.error(this.translate.instant('events.metric_errored'));
        });
    } else {
      metric = { ...this.data.edit, ...metric };
      this.metricService.setMetric(metric).pipe(takeUntil(this.destroy$)).subscribe((res) => {
        this.pop.toast(this.translate.instant('events.metric_saved'), 'success', 4000, 'save');
        this.dialogRef.close(metric);
      },
        (error) => {
          this.pop.error(this.translate.instant('events.metric_errored'));
        });
    }
  }

  loadMetrics(event: any) {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];

      this.uploadError = '';
      this.uploadSuccess = '';
      this.uploadedFileName = file.name;

      this.uploadInProgress = true;
      this.uploadProgress = 0;

      this.metricService.uploadMetrics(file, { type: '.csv' }).pipe(takeUntil(this.destroy$)).subscribe((res: any) => {
        if (res.loaded) {
          this.uploadProgress = (res.loaded / res.total) * 100;

          if (this.uploadProgress == 100) {
            this.uploadInProgress = false;
            this.uploadSuccess = `Votre fichier ${this.uploadedFileName} a été uploadé avec succés !`;
          }
        }
      },

        (err: any) => {
          console.error('Upload failed', err);
          if (err.status === 422) {
            this.uploadError = `Erreur : ${err.error.error}`;
          } else {
            this.uploadError = `Votre fichier ${this.uploadedFileName} n'a pas pu être uploadé !`;
          }
          this.uploadInProgress = false;
        }
      );
    }
  }

  exportMetric() {
    const metrics = this.currentModel?.payload || [];
    const rows: string[] = [];

    const header = metrics.map((metric: any) => metric.label).join(";");
    rows.push(header);

    const dataRow = metrics.map((metric: any) => metric.value || "").join(";");
    rows.push(dataRow);

    const csvContent = rows.join("\n");
    const bom = "\uFEFF";

    const blob = new Blob([bom + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "metrics.csv");
    document.body.appendChild(link);
    link.click();

    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  goToSettings() {
    this.dialogRef.close();
    this.router.navigate(['/settings']);
  }

}
